import type { SxProps, Theme } from '@mui/material/styles';
import { STANDARD_HEADER_HEIGHT } from '../../Theme/constants';

/**
 * Common Container styling patterns extracted from mobile app usage.
 *
 * These patterns appear in 5 page-level layouts and can be reused via the sx prop.
 * Exported as reusable style objects to promote consistency across applications.
 *
 * @example
 * import { Container, responsiveContainerNoPaddingStyles } from '@planitgeo/treeplotter-ui-library';
 *
 * <Container sx={responsiveContainerNoPaddingStyles}>
 *   <PageContent />
 * </Container>
 */

/**
 * Responsive container with no horizontal padding.
 *
 * Used in page layouts where content should extend to container edges
 * at all breakpoints. Common for ErrorPage, SettingsPage, and similar layouts.
 *
 * Pattern from mobile app sharedStyles['responsive-container-no-padding'].
 *
 * @example
 * <Container sx={responsiveContainerNoPaddingStyles}>
 *   <SettingsForm />
 * </Container>
 */
export const responsiveContainerNoPaddingStyles: SxProps<Theme> = {
  px: { xs: 0, sm: 0, md: 0, lg: 0, xl: 0 },
};

/**
 * Fixed form container positioning.
 *
 * Used for form pages that need fixed positioning below a header.
 * The container is positioned fixed with a top margin equal to the header height.
 * Common for NewPage and RecordEditablePage.
 *
 * Pattern from mobile app sharedStyles['form-container'].
 *
 * @example
 * <Container sx={formContainerStyles}>
 *   <AssetEditForm />
 * </Container>
 */
export const formContainerStyles: SxProps<Theme> = {
  position: 'fixed',
  marginTop: `${STANDARD_HEADER_HEIGHT}px`,
  padding: 0,
};

/**
 * Page container with vertical margin.
 *
 * Used for standalone pages that need vertical separation from the viewport top.
 * Common for NoTenantPage and similar standalone informational pages.
 *
 * @example
 * <Container sx={pageContainerWithMarginStyles} maxWidth="xl">
 *   <NoTenantContent />
 * </Container>
 */
export const pageContainerWithMarginStyles: SxProps<Theme> = {
  marginTop: '10vh',
};

/**
 * Centered content container with standard vertical padding.
 *
 * Default pattern for general page content with comfortable vertical spacing.
 *
 * @example
 * <Container sx={centeredContentContainerStyles} maxWidth="lg">
 *   <Typography variant="h1">Welcome</Typography>
 *   <Content />
 * </Container>
 */
export const centeredContentContainerStyles: SxProps<Theme> = {
  py: 3,
};
